import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, BadgeCheck } from "lucide-react";
import { motionOk } from "../lib/motionOk.js";
import { TiltCard } from "../lib/Interactive.jsx";
import { useLang } from "../i18n.jsx";

export default function Solutions() {
  const { content, waLink } = useLang();
  const { solutions, solutionsHead, forLabel, solutionCta, previewLabel } = content;
  return (
    <section id="solusi" className="relative px-6 py-20 overflow-hidden">
      <div className="pointer-events-none absolute -left-24 top-1/3 w-[440px] h-[440px] rounded-full bg-acid/[0.04] blur-3xl" />
      <div className="relative mx-auto max-w-7xl">
        <motion.div
          initial={motionOk ? { opacity: 0, y: 24 } : false}
          whileInView={motionOk ? { opacity: 1, y: 0 } : undefined}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-2xl"
        >
          <p className="mb-4 text-[12px] uppercase tracking-[0.3em] text-acid">{solutionsHead.eyebrow}</p>
          <h2 className="font-display font-extrabold tracking-tight text-[clamp(1.8rem,4.2vw,3.2rem)] leading-[1.08]">
            {solutionsHead.titleA}
            <span className="block">{solutionsHead.titleB}</span>
          </h2>
        </motion.div>

        <div className="mt-12 grid gap-7 lg:grid-cols-3">
          {solutions.map((t, i) => (
            <motion.article
              key={t.id}
              initial={motionOk ? { opacity: 0, y: 34 } : false}
              whileInView={motionOk ? { opacity: 1, y: 0 } : undefined}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.1, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="group flex flex-col rounded-[26px] border border-paper/10 bg-ink2/60 p-3 pb-7 transition-colors"
            >
              <div className="relative">
                <TiltCard max={5} className="relative overflow-hidden rounded-[20px] border border-paper/10">
                  <span className="absolute left-3 top-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-ink/85 backdrop-blur px-3 py-1.5 text-[11px] font-bold text-on-dark">
                    <BadgeCheck className="w-3.5 h-3.5" style={{ color: t.accent }} /> {t.status}
                  </span>
                  <span className="absolute right-3 top-3 z-10 rounded-full px-2 py-1 text-[10px] font-bold uppercase text-ink" style={{ background: t.accent }}>
                    {t.index}
                  </span>
                  <div className="relative overflow-hidden">
                    <img src={t.img} alt={t.name} loading="lazy" className="aspect-[16/11] w-full object-cover opacity-90 transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <span className="pointer-events-none absolute inset-0 grid place-items-center opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    <span className="rounded-full bg-acid px-5 py-2.5 text-sm font-bold text-ink shadow-2xl">{previewLabel}</span>
                  </span>
                </TiltCard>
              </div>
              <div className="flex flex-1 flex-col px-3 pt-5">
                <span className="text-[11px] uppercase tracking-[0.2em] text-acid">{forLabel}</span>
                <h3 className="mt-1 font-display text-xl font-extrabold tracking-tight text-paper">{t.name}</h3>
                <p className="mt-0.5 text-paper/65 text-sm">{t.for}</p>
                <p className="mt-3 text-sm text-paper/70 leading-relaxed">{t.focus}</p>
                <p className="mt-3 text-[14px] text-paper/65 leading-relaxed">{t.desc}</p>
                <a
                  href={waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-cursor
                  className="mt-6 flex w-full items-center justify-center gap-2 rounded-full border border-paper/20 py-3 text-sm font-semibold text-paper transition-colors hover:border-acid hover:bg-acid hover:text-ink"
                >
                  {solutionCta} <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
